import type { GetRequestsListItem } from "@nlc/shared-types";

import type { TableConfig } from "@/features/requests/types/tables";

import { COLUMN_WIDTHS } from "@/features/requests/config/constants/columnWidths";
import {
  inlineAssigneeCell,
  inlineBrandCell,
  inlineComplexityCell,
  inlineDueDateCell,
  inlineStatusCell,
} from "@/features/requests/config/helpers/cellRenderers";
import {
  assigneeCell,
  brandCell,
  complexityCell,
  dateCell,
  nameCell,
  queuedNameCell,
  requestDesignTypeCell,
} from "@/features/requests/config/helpers/commonCells";
import { whitelabelRowClassName } from "@/features/requests/constants/accountTags";

export const projectRequestsTableConfig: TableConfig<GetRequestsListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: COLUMN_WIDTHS.name,
    },
    {
      accessor: "assignee",
      cellRenderer: inlineAssigneeCell,
      header: "Assignee",
      key: "assignee",
      sortable: true,
      width: COLUMN_WIDTHS.assignee,
    },
    {
      accessor: "dueDate",
      cellRenderer: inlineDueDateCell,
      header: "Due",
      key: "dueDate",
      sortable: true,
      width: COLUMN_WIDTHS.dueDate,
    },
    {
      accessor: "status",
      cellRenderer: inlineStatusCell,
      header: "Status",
      key: "status",
      sortable: true,
      width: COLUMN_WIDTHS.status,
    },
    {
      accessor: "complexity",
      cellRenderer: inlineComplexityCell,
      header: "Level",
      key: "complexity",
      sortable: true,
      width: COLUMN_WIDTHS.complexity,
    },
    {
      accessor: "brand",
      cellRenderer: inlineBrandCell,
      header: "Brand",
      key: "brand",
      sortable: true,
      width: COLUMN_WIDTHS.brand,
    },
    {
      accessor: "createdAt",
      cellRenderer: dateCell,
      header: "Created on",
      key: "createdAt",
      sortable: true,
      width: COLUMN_WIDTHS.createdAt,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request/Design type",
      key: "requestType",
      sortable: true,
      width: COLUMN_WIDTHS.requestType,
    },
  ],
  defaultSort: {
    column: "createdAt",
    direction: "desc",
  },
  enablePagination: true,
  enableSorting: true,
  getRowClassName: whitelabelRowClassName,
  id: "project-requests",
};

export const projectRequestsDesignerTableConfig: TableConfig<GetRequestsListItem> =
  {
    ...projectRequestsTableConfig,
    columns: projectRequestsTableConfig.columns.map((col) => {
      if (col.key === "assignee") return { ...col, cellRenderer: assigneeCell };
      if (col.key === "complexity")
        return { ...col, cellRenderer: complexityCell };
      if (col.key === "brand") return { ...col, cellRenderer: brandCell };

      return col;
    }),
  };

export const projectDraftsRequestsTableConfig: TableConfig<GetRequestsListItem> =
  {
    ...projectRequestsTableConfig,
    columns: projectRequestsTableConfig.columns.filter(
      (col) =>
        col.key !== "assignee" && col.key !== "dueDate" && col.key !== "status",
    ),
    id: "project-drafts-requests",
  };

export const projectQueuedRequestsTableConfig: TableConfig<GetRequestsListItem> =
  {
    columns: [
      {
        accessor: "name",
        cellRenderer: queuedNameCell,
        header: "Name",
        key: "name",
        sortable: false,
        width: COLUMN_WIDTHS.name,
      },
      {
        accessor: "assignee",
        cellRenderer: inlineAssigneeCell,
        header: "Assignee",
        key: "assignee",
        sortable: false,
        width: COLUMN_WIDTHS.assignee,
      },
      {
        accessor: "dueDate",
        cellRenderer: inlineDueDateCell,
        header: "Due",
        key: "dueDate",
        sortable: false,
        width: COLUMN_WIDTHS.dueDate,
      },
      {
        accessor: "status",
        cellRenderer: inlineStatusCell,
        header: "Status",
        key: "status",
        sortable: false,
        width: COLUMN_WIDTHS.status,
      },
      {
        accessor: "complexity",
        cellRenderer: inlineComplexityCell,
        header: "Level",
        key: "complexity",
        sortable: false,
        width: COLUMN_WIDTHS.complexity,
      },
      {
        accessor: "brand",
        cellRenderer: inlineBrandCell,
        header: "Brand",
        key: "brand",
        sortable: false,
        width: COLUMN_WIDTHS.brand,
      },
      {
        accessor: "createdAt",
        cellRenderer: dateCell,
        header: "Created on",
        key: "createdAt",
        sortable: false,
        width: COLUMN_WIDTHS.createdAt,
      },
      {
        accessor: "requestType",
        cellRenderer: requestDesignTypeCell,
        header: "Request/Design type",
        key: "requestType",
        sortable: false,
        width: COLUMN_WIDTHS.requestType,
      },
    ],
    enableDragAndDrop: true,
    enablePagination: false,
    enableSorting: false,
    getRowClassName: whitelabelRowClassName,
    id: "project-queued-requests",
  };
